import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Banknote, Building, Calendar, Edit, LayoutDashboard, Mail, MessageSquarePlus, MoveRight, Percent, Phone, User, UserCheck, UserCog, UserPlus, } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../states/slices/resourceSlice';
import { getBranchByCompanyId, getNotificationContactsByCompanyId } from '../../api/requests';
import usePartner from '../../hooks/partnerHook';
import { useQuery } from '@tanstack/react-query';
import { Company } from '../../models/company/Company';
import Loader from '../../components/Loader';
import PolicyStatus from '../../components/elements/PolicyStatus';
import { capitalizeSentence, getPhoneNumberFromShortCodeFormat, sortArrayByAlphabets } from '../../utils/miscFunctions';
import Branch from '../shared/settings/branch';
import FinancePartnerDashboard from '../shared/dashboard/financePartnerDashboard';
import { COMPANY_TYPES } from '../../resources/enums';
import InsurerManagementDashboard from '../admin/dashboard/insurerDashboard/InsurerManagementDashboard';
import BrokerDashboard from '../shared/dashboard/broker';
import TeamMembers from './TeamMembers';
import useRoute from '../../hooks/routesHook';
import BackButton from '../../components/BackButton';
import TabNav, { ITab } from '../../components/tabs/TabNav';
import TabContent from '../../components/tabs/TabContent';
import Performance from './Performance';
import SalesTable from './insurers/insurerDetails/SalesTable';
import CompanyPolicies from './CompanyPolicies';
import useUserAccount from '../../hooks/userAccountHook';
import PopOnMe from '../../components/elements/poponme/PopOnMe';
import Button from '../../components/form-control/Button';
import Payouts from './shared/payouts';

// singleCompany && singleCompany type
type CompanyType = 'insurance_company' | 'broker' | 'financing_partner' | 'other';

// singleCompany && singleCompany status
type CompanyStatus = 'active' | 'inactive' | 'pending';


// Branch interface
interface Branch {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  memberCount: number;
  companyId: string;
}

// Branch member interface
interface BranchMember {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  joinedAt: string;
  branchId: string;
}

const CompanyDetail: React.FC = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState<any>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [branchMembers, setBranchMembers] = useState<BranchMember[]>([]);
  const { getDashboardBase } = useRoute();
  const [selectedTab, setSelectedTab] = useState<string>('overview')
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { user } = useUserAccount();
  const tab = queryParams.get('tab');
  const { id } = useParams();
  const activeTab = tab ? tab : 'overview';
  const { inviteMember, editTeam, assignRelationshipManager, addTeamToSlackWorkspace, PartnerModal, getSingleCompany, assignTeamLead, generateAnAgencyPayout, setCommissionPercentage, assignMember } = usePartner();
  const countries = useSelector(getSelectedCountry)


  const getSingleCompanyBranch = async () => {
    await getBranchByCompanyId(Number(id))
      .then((response: any) => {
        setBranches(response.data.data)
      })
      .catch((error: Error) => {
        console.log("error", error)
      })
      .finally(() => { })
  }

  const { data: singleCompany, isLoading: singleCompanyLoading } = useQuery({
    queryKey: ['getSingleCompany', id],
    queryFn: () => getSingleCompany(id ?? ''),
    enabled: !!id
  })

  useQuery({
    queryKey: ['getSingleCompanyBranch'],
    queryFn: getSingleCompanyBranch
  })

  const { data: notificationContacts } = useQuery<Array<{ id: number; email: string; phone: string }>>({
    queryKey: ['getNotificationContactsByCompanyId', id],
    queryFn: async () => {
      const response: any = await getNotificationContactsByCompanyId(Number(id));
      const raw = Array.isArray(response) ? response : response?.data ?? [];
      return raw.map((c: any) => ({ id: c.id, email: c.email ?? '', phone: c.phone ?? '' }));
    },
    enabled: !!id && activeTab === 'users',
  });

  const handleBack = () => {
    navigate(`${getDashboardBase()}/teams`);
  };


  const overview = (type: string) => {

    switch (type) {
      case COMPANY_TYPES.FINANCIER:
        return <FinancePartnerDashboard isCompany={true} />
      case COMPANY_TYPES.INSURER:
        return <InsurerManagementDashboard isCompany={true} id={Number(id)} />
      case COMPANY_TYPES.BROKER:
        return <BrokerDashboard companyId={Number(id)} />
      case COMPANY_TYPES.AGENCY:
        return <BrokerDashboard companyId={Number(id)} />

      default:
        return <div>No Overview available</div>
    }
  }

  const getTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return singleCompany && overview(singleCompany?.type)
      case 'branches':
        return <Branch branchesProp={branches} />
      case 'users':
        return <TeamMembers teamID={Number(id)} />
    }
  }

  const getTabs = () => {
    let tabs: ITab[] = [
      {
        id: 'overview',
        title: 'Overview'
      },
      {
        id: 'branches',
        title: 'Branches'
      },
      {
        id: 'users',
        title: 'Users'
      },
    ];

    return tabs;
  };

  if (singleCompanyLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
          <Loader message="Loading page. Please wait..." brollyLoaderFixed  />
      </div>
    );
  }

  const getSingleTeamActions = () => {
    const singleTeamActions = [
      {
        label: 'Edit company information',
        icon: <Edit className="h-4 w-4" />,
        callback: () => {
          editTeam(singleCompany);
        }
      },

      {
        label: 'Invite new member',
        icon: <UserPlus className="h-4 w-4" />,
        callback: () => {
          inviteMember(singleCompany, 'getSingleCompany');
        }
      },
    ];

    if (user.isOwner()) {
      singleTeamActions.push(
        {
          label: 'Set relationship manager',
          icon: <UserCheck className="h-4 w-4" />,
          callback: () => {
            assignRelationshipManager(singleCompany);
          }
        },
        {
          label: 'Assign company admin',
          icon: <UserCog className="h-4 w-4" />,
          callback: () => {
            singleCompany?.id && assignTeamLead(singleCompany?.id);
          }
        },
        {
          label: 'Add to workspace',
          icon: <MessageSquarePlus className="h-4 w-4" />,
          callback: () => {
            addTeamToSlackWorkspace(singleCompany);
          }
        },
        {
          label: 'Move member to company',
          icon: <MoveRight className="h-4 w-4" />,
          callback: () => {
            singleCompany?.id && assignMember(singleCompany?.id);
          }
        },
        {
          label: 'Set commission rate',
          icon: <Percent className="h-4 w-4" />,
          callback: () => {
            singleCompany?.id && setCommissionPercentage(singleCompany?.id);
          }
        },

        {
          label: 'Generate payout',
          icon: <Banknote className="h-4 w-4" />,
          callback: () => {
            singleCompany?.id && generateAnAgencyPayout(singleCompany?.id);
          }
        }
      );
    }

    return sortArrayByAlphabets(singleTeamActions);
  };

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <div className=" mb-6 flex justify-between w-full">
          <BackButton onClick={handleBack} />

          {getSingleTeamActions()?.length > 0 && (
            <div className='relative'>
              <div className="cursor-pointer border border-primary-main hover:bg-primary-main/80 rounded-full py-1 px-3">
                <PopOnMe actions={getSingleTeamActions()}>
                  <span className="dark:text-darkBg-mainText">Actions</span>
                </PopOnMe>
              </div>
            </div>
          )}

        </div>

        <div className="bg-white dark:bg-darkBg rounded-xl shadow-md overflow-hidden mb-6 border border-gray-100 dark:border-darkBg-border">
          <div className="h-2 bg-gradient-to-r from-yellow-500 to-yellow-600"></div>
          <div className="p-6 sm:p-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div className="flex items-center">
                <div className="h-20 w-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-darkBg dark:to-darkBg-border rounded-2xl flex items-center justify-center mr-5 shadow-sm border border-gray-100 dark:border-darkBg-border">
                  {singleCompany && singleCompany.logo ? (
                    <img src={singleCompany && singleCompany.logo} alt={singleCompany && singleCompany.name} className="h-16 w-16 rounded-xl" />
                  ) : (
                    <Building className="h-10 w-10 text-primary-main opacity-80" />
                  )}
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 dark:from-darkBg-mainText dark:to-darkBg-subText">{singleCompany && singleCompany.name}</h1>
                  <div className="flex flex-wrap items-center mt-2 gap-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full`}>
                      <PolicyStatus
                        status={
                          singleCompany?.type ? capitalizeSentence(singleCompany.type.split('_').join(' ')) : '-'
                        }
                        classes="px-2  !w-fit"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* singleCompany && singleCompany details grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
              <div className="bg-gradient-to-br from-white to-blue-50 dark:from-darkBg dark:to-darkBg-border border dark:border-darkBg-border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center">
                  <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-full p-2.5 mr-3">
                    <Mail className="h-5 w-5 text-primary-main" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-darkBg-subText">Email</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">{singleCompany && singleCompany.email}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-blue-50 dark:from-darkBg dark:to-darkBg-border border border-gray-100 dark:border-darkBg-border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center">
                  <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-full p-2.5 mr-3">
                    <Phone className="h-5 w-5 text-primary-main" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-darkBg-subText">Phone</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">{singleCompany?.mobilePhone ? getPhoneNumberFromShortCodeFormat(singleCompany.mobilePhone) : '-'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-blue-50 dark:from-darkBg dark:to-darkBg-border border border-gray-100 dark:border-darkBg-border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center">
                  <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-full p-2.5 mr-3">
                    <Calendar className="h-5 w-5 text-primary-main" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-darkBg-subText">Created</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">
                      {singleCompany && singleCompany.createdOn ? new Date(singleCompany.createdOn).toLocaleDateString() : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-darkBg rounded-xl shadow-md mb-6 border border-gray-100 dark:border-darkBg-border overflow-hidden">

          <div className="px-6 flex items-center gap-5 mt-5">
            <TabNav
              tabs={getTabs()}
              activeTab={activeTab}
              page={`teams/${id}`}
              setActiveTab={(id: string) => { }}
            />
          </div>

          {/* Tab content */}
          <div className="p-6">
            <TabContent id={activeTab} activeTab={activeTab}>
              <div>
                {activeTab === 'users' && (
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-darkBg-mainText flex items-center">
                        <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded mr-3"></div>
                        Notification Recipients
                      </h2>
                    </div>
                    {notificationContacts && notificationContacts.length > 0 ? (
                      <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-darkBg-border">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-darkBg-cardBg text-gray-500 dark:text-darkBg-subText uppercase text-xs">
                            <tr>
                              <th className="px-4 py-3 text-left font-medium">Email</th>
                              <th className="px-4 py-3 text-left font-medium">Phone</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-darkBg-border text-gray-900 dark:text-darkBg-mainText">
                            {notificationContacts.map((contact) => (
                              <tr key={contact.id}>
                                <td className="px-4 py-3">{contact.email || '-'}</td>
                                <td className="px-4 py-3">
                                  {contact.phone ? getPhoneNumberFromShortCodeFormat(contact.phone) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-darkBg-subText">
                        No notification recipients have been added.
                      </p>
                    )}
                  </div>
                )}

                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-darkBg-mainText flex items-center">
                    <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded mr-3"></div>
                    Company {activeTab}
                  </h2>
                </div>

                <div className="grid grid-cols-1 ">
                  {getTabContent()}
                </div>

              </div>
            </TabContent>

          </div>

        </div>

      </div>
      {PartnerModal()}
    </>
  )
};

export default CompanyDetail;
